import { Locale, convertLanguage } from '@frontend/common';
import { ObjectBadgeMapper } from '@frontend/components/common';
import { RelativeDateBadge } from '@frontend/elements';
import { getCorrectTranslation, useLocale } from '@frontend/lang';
import { CellContext, Row } from '@tanstack/react-table';
import React, { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LuAsterisk } from 'react-icons/lu';

import { ObjectColumnsProps, defaultCellClassName } from '../object-column-mapper';
import { DateTimeCell } from '../table/cell/date-time-cell/date-time-cell.component';
import { AsyncBlobCell } from './async-blob-cell';
import { AsyncDocumentCell } from './async-document-cell';
import { BooleanCell } from './boolean-cell';

export interface ObjectCellMapperProps<T extends { id: string } & object> extends CellContext<T, any> {
    className?: string | ((row: Row<T>) => string);
    parent: ObjectColumnsProps<T>;
    cellKey: keyof T | string;
}

export const ObjectCellMapper = <T extends { id: string } & object>(props: ObjectCellMapperProps<T>) => {
    const { locale } = useLocale();
    const tooltipId = useId().replace(/:/g, '');
    if (typeof props.cellKey === 'string' && props.parent.customCells && props.parent.customCells.has(props.cellKey)) {
        return <>{props.parent.customCells.get(props.cellKey)!(props)}</>;
    }

    const userLocale = locale ? locale.toLowerCase().replace(/-/g, '_') : undefined;
    const cellClassName = `${props.className ? (typeof props.className === 'string' ? props.className : props.className(props.row)) : defaultCellClassName}`;
    if (
        Object.keys(props.row.original).includes((props.cellKey as string) + '_' + userLocale) &&
        !props.parent.ignoreTranslations?.includes(props.cellKey as keyof T)
    ) {
        return (
            <td className={cellClassName ?? 'text-foreground-default self-start text-sm font-normal'}>
                {getCorrectTranslation(locale, props.row.original, props.cellKey as keyof T)}
            </td>
        );
    } else {
        const key =
            props.parent.valueOverwrite && props.parent.valueOverwrite.has(props.cellKey as keyof T)
                ? props.parent.valueOverwrite.get(props.cellKey as keyof T)!
                : props.cellKey;
        const obj = props.row.original;
        if (typeof key != 'string') return <></>;
        if (props.parent.blobColumnOverwrite && props.parent.blobColumnOverwrite.has(key as keyof T)) {
            return (
                <AsyncBlobCell
                    object={obj}
                    fetchBlob={props.parent.blobColumnOverwrite.get(key as keyof T)!}
                />
            );
        }
        if (props.parent.documentColumnOverwrite && props.parent.documentColumnOverwrite.key === key) {
            return (
                <AsyncDocumentCell
                    object={obj}
                    objectKey={props.parent.documentColumnOverwrite!.key}
                    entityType={props.parent.documentColumnOverwrite!.entityType}
                />
            );
        }
        if (props.parent.entityColumnOverwrite && props.parent.entityColumnOverwrite.has(key as keyof T)) {
            const extraProps = props.parent.entityColumnOverwriteProps?.get(key as keyof T);
            const type = (obj[props.parent.entityColumnOverwrite.get(key as keyof T)!] as string)?.endsWith('_id')
                ? (obj[props.parent.entityColumnOverwrite.get(key as keyof T)!] as string)
                : (obj[props.parent.entityColumnOverwrite.get(key as keyof T)!] as string) + '_id';
            return (
                <td className={cellClassName ?? defaultCellClassName}>
                    <ObjectBadgeMapper
                        id={obj[key as keyof T] as string}
                        type={type}
                        {...extraProps}
                    />
                </td>
            );
        }
        if (key === 'id') {
            if (props.parent.showIdAsEntity) {
                return (
                    <td className={cellClassName ?? defaultCellClassName}>
                        <ObjectBadgeMapper
                            id={obj[key as keyof T] as string}
                            type={props.parent.showIdAsEntity + '_id'}
                        />
                    </td>
                );
            }
            return (
                <td className={cellClassName || 'text-foreground-default content-center self-start py-3 pr-4 text-sm font-normal'}>
                    <p>{obj[key] as string}</p>
                </td>
            );
        }
        if (key.includes('_id') && key.length > 2 && key !== 'forbidden' && !key.includes('identifier')) {
            return (
                <td className={cellClassName ?? defaultCellClassName}>
                    <ObjectBadgeMapper
                        id={obj[key as keyof T] as string}
                        type={key}
                    />
                </td>
            );
        }
        if (key === 'heartbeat_timestamp') {
            return (
                <td className={cellClassName ?? defaultCellClassName}>
                    <RelativeDateBadge
                        date={obj[key as keyof T] as string}
                        displayTime
                        inline
                        emptyValue='-'
                    />
                </td>
            );
        }
        if (key.includes('date') || key.includes('timestamp')) {
            return (
                <DateTimeCell
                    className={cellClassName}
                    date={obj[key as keyof T] as string}
                    displayTime={key.includes('time')}
                    inline
                />
            );
        }
        if (typeof obj[key as keyof T] === 'boolean') {
            return (
                <BooleanCell
                    className={cellClassName}
                    value={obj[key as keyof T] as boolean}
                />
            );
        }
        if (key === 'secret') {
            const icon = <LuAsterisk size={10} />;
            return <td className={cellClassName}>{Array(8).fill(icon)}</td>;
        }
        if (key === 'language') {
            return <td className={cellClassName}>{convertLanguage(obj[key as keyof T] as Locale)}</td>;
        }
        if (Array.isArray(obj[key as keyof T])) {
            const content = obj[key as keyof T] as unknown[];
            const tooltipLabels = content.map(formatArrayTooltipLabel).filter(Boolean);

            if (content.length === 0) {
                return <td className={cellClassName}>{0}</td>;
            }

            return (
                <OverlayTrigger
                    placement='top'
                    delay={{ show: 300, hide: 150 }}
                    popperConfig={{ strategy: 'fixed' }}
                    overlay={
                        <Tooltip
                            id={`${tooltipId}-${props.row.id}-${String(key)}`}
                            className='border-border-default bg-background-surface z-1000 rounded-md border shadow-sm'>
                            <span className='text-foreground-default block px-2 py-1 whitespace-pre-line'>{tooltipLabels.join('\n')}</span>
                        </Tooltip>
                    }>
                    <td
                        className={cellClassName}
                        tabIndex={0}>
                        {content.length}
                    </td>
                </OverlayTrigger>
            );
        }
        if (typeof obj[key as keyof T] === 'string') {
            return <td className={cellClassName}>{(obj[key as keyof T] as string) ?? ''}</td>;
        }

        if (obj[key as keyof T] === null) {
            return <td className={cellClassName}></td>;
        }
        return <td className={cellClassName}>{JSON.stringify(obj[key as keyof T])}</td>;
    }
};

const formatArrayTooltipLabel = (item: unknown): string => {
    if (item === null || item === undefined) {
        return '';
    }

    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        return String(item);
    }

    if (typeof item === 'object' && 'name' in item && typeof item.name === 'string') {
        return item.name;
    }

    return JSON.stringify(item);
};
